import type { ComponentType, HTMLAttributes } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Loader,
  QrCode,
  Unplug,
} from "lucide-react";
import { cn } from "../lib/cn";

/** Os 6 estados de saúde do monitorador (DESIGN_SYSTEM seção 8). */
export type MonitorStatus =
  | "CONNECTED"
  | "CONNECTING"
  | "DISCONNECTED"
  | "ERROR"
  | "QR_REQUIRED"
  | "DEGRADED";

interface StatusSpec {
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** cor do texto/ícone */
  text: string;
  /** fundo suave da cápsula (o mesmo tom, a 16%) */
  soft: string;
  /** tratamento do dot: sólido, vazado, pulsante, anel */
  dot: "solid" | "outline" | "pulse" | "ring";
}

/**
 * MESMA anatomia nos 6 estados (dot 8px + ícone 14px + texto): o estado se
 * reconhece pela FORMA, não só pela cor — daltônico, impressão p&b e a
 * olhada de 1 segundo às 23h no celular continuam funcionando.
 */
export const MONITOR_STATUS: Record<MonitorStatus, StatusSpec> = {
  CONNECTED: { label: "Conectado", icon: CheckCircle2, text: "text-success", soft: "bg-success-soft", dot: "solid" },
  CONNECTING: { label: "Conectando…", icon: Loader, text: "text-info", soft: "bg-info-soft", dot: "pulse" },
  DISCONNECTED: {
    // neutro de propósito: desconectado não é erro ativo
    label: "Desconectado",
    icon: Unplug,
    text: "text-muted-foreground",
    soft: "bg-muted",
    dot: "outline",
  },
  ERROR: { label: "Erro", icon: AlertTriangle, text: "text-danger", soft: "bg-danger-soft", dot: "solid" },
  QR_REQUIRED: { label: "Escanear QR", icon: QrCode, text: "text-warning", soft: "bg-warning-soft", dot: "solid" },
  DEGRADED: { label: "Instável", icon: Activity, text: "text-warning", soft: "bg-warning-soft", dot: "ring" },
};

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  status: MonitorStatus;
  /** Sobrescreve o rótulo padrão (ex.: "Conectado há 3h"). */
  label?: string;
  /** Só o dot + ícone, com o rótulo em `aria-label` (sidebar colapsada). */
  compact?: boolean;
  /** Sem a cápsula de fundo — para dentro de linha densa de tabela/lista. */
  plain?: boolean;
}

export function StatusPill({
  status,
  label,
  compact = false,
  plain = false,
  className,
  ...props
}: StatusPillProps) {
  const spec = MONITOR_STATUS[status];
  const Icon = spec.icon;
  const text = label ?? spec.label;

  return (
    <span
      data-status={status}
      className={cn(
        "inline-flex items-center gap-1.5 align-middle text-2xs font-medium whitespace-nowrap",
        !plain && "h-5.5 rounded-pill pl-2 pr-2.5",
        !plain && spec.soft,
        spec.text,
        className,
      )}
      aria-label={compact ? text : undefined}
      title={compact ? text : undefined}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-1.75 shrink-0 rounded-full",
          spec.dot === "solid" && "bg-current",
          spec.dot === "outline" && "border border-current bg-transparent",
          spec.dot === "pulse" && "animate-pulse bg-current motion-reduce:animate-none",
          spec.dot === "ring" &&
            "bg-current ring-2 ring-current/30 animate-pulse motion-reduce:animate-none",
        )}
      />
      <Icon
        aria-hidden="true"
        className={cn(
          "size-3.25 shrink-0",
          status === "CONNECTING" && "animate-spin [animation-duration:2s] motion-reduce:animate-none",
        )}
      />
      {compact ? <span className="sr-only">{text}</span> : <span>{text}</span>}
    </span>
  );
}

/** Severidade de incidente (seção 8) — faixa vertical + badge + ícone. */
export type IncidentSeverity = "INFO" | "WARNING" | "CRITICAL";

export interface SeverityBarProps extends HTMLAttributes<HTMLDivElement> {
  severity: IncidentSeverity;
}

// tom 500 da rampa: a barra é marca gráfica, não texto — pede a cor viva
const SEVERITY_BAR: Record<IncidentSeverity, string> = {
  INFO: "bg-blue-500",
  WARNING: "bg-yellow-500",
  CRITICAL: "bg-red-500",
};

/**
 * Faixa de 3px à esquerda do item de lista/notificação.
 * Não pinta o card inteiro — isso é anti-padrão (seção 12).
 */
export function SeverityBar({ severity, className, ...props }: SeverityBarProps) {
  return (
    <div
      aria-hidden="true"
      className={cn("w-0.75 shrink-0 self-stretch rounded-xs", SEVERITY_BAR[severity], className)}
      {...props}
    />
  );
}
